import React, { createContext, useState } from 'react';
import axios from 'axios';
import config from '../config/config';

export const MedicaidTestContext = createContext();

export function MedicaidTestProvider(props) {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [runResults, setRunResults] = useState(null);
  const [permitted, setPermitted] = useState(null);

  const checkPermission = async () => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/medicaid-tests/check-permission`);
      setPermitted(response.data.permitted === true);
      return true;
    } catch (err) {
      console.error('Medicaid Tests permission check failed:', err);
      setPermitted(false);
      return false;
    }
  };

  const getScenarios = async (state) => {
    setLoading(true);
    setError(null);
    try {
      const url = state
        ? `${config.apiUrl}/api/medicaid-tests?state=${encodeURIComponent(state)}`
        : `${config.apiUrl}/api/medicaid-tests`;
      const response = await axios.get(url);
      setScenarios(response.data || []);
    } catch (err) {
      console.error('Error fetching Medicaid test scenarios:', err);
      setError(err.response?.data?.error || 'Failed to fetch scenarios');
    } finally {
      setLoading(false);
    }
  };

  const generateBaseline = async (id, baselineRuns = 3) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${config.apiUrl}/api/medicaid-tests/${id}/generate-baseline`,
        { baselineRuns }
      );
      setScenarios(prev =>
        prev.map(s =>
          s._id === id
            ? {
                ...s,
                latest_baseline_summary: response.data.latest_baseline_summary,
                baseline_tolerance_summary: response.data.baseline_tolerance_summary,
              }
            : s
        )
      );
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error generating baseline:', err);
      const errorMsg = err.response?.data?.error || 'Failed to generate baseline';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  const updateScenario = async (id, fields) => {
    setError(null);
    try {
      const response = await axios.patch(
        `${config.apiUrl}/api/medicaid-tests/${id}`,
        fields
      );
      setScenarios(prev => prev.map(s => (s._id === id ? response.data : s)));
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error updating scenario:', err);
      const errorMsg = err.response?.data?.error || 'Failed to update scenario';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const deleteScenario = async (id) => {
    setError(null);
    try {
      await axios.delete(`${config.apiUrl}/api/medicaid-tests/${id}`);
      setScenarios(prev => prev.filter(s => s._id !== id));
      return { success: true };
    } catch (err) {
      console.error('Error deleting scenario:', err);
      const errorMsg = err.response?.data?.error || 'Failed to delete scenario';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const runTests = async (scenarioIds) => {
    setLoading(true);
    setError(null);
    setRunResults(null);
    try {
      const response = await axios.post(
        `${config.apiUrl}/api/medicaid-tests/run`,
        { scenarioIds }
      );
      setRunResults(response.data);
      return { success: true, data: response.data };
    } catch (err) {
      console.error('Error running tests:', err);
      const errorMsg = err.response?.data?.error || 'Failed to run tests';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  return (
    <MedicaidTestContext.Provider
      value={{
        scenarios,
        loading,
        error,
        runResults,
        permitted,
        setError,
        checkPermission,
        getScenarios,
        generateBaseline,
        runTests,
        setRunResults,
        updateScenario,
        deleteScenario,
      }}
    >
      {props.children}
    </MedicaidTestContext.Provider>
  );
}
